import { useState } from "react";
import type { ReactElement } from "react";

import { Button, Dialog, Icon, Modal } from "../design-system";
import { Aborted, forgetPasskey, passkeyEnrolled, setUpPasskey } from "../session/passkey";
import { Notice, describe } from "../views/loading";
import type { PasskeyApi } from "../api/client";

/**
 * **Einstellungen ▸ Anmeldedaten ändern** — der Ort des biometrischen Zugangs
 * (`FRAME.md`, „Benutzermenü"; #159).
 *
 * Er wohnt hier und nicht unter „Geräteeinstellungen": Der biometrische Zugang
 * ist eine **Anmeldeart** und keine Geräteeinstellung — wer ihn sucht, sucht ihn
 * bei seinen Anmeldedaten, neben Passwort und Zweitfaktor. Dass er zugleich an
 * diesem Gerät und an der Adresse der Anwendung hängt, ist eine Eigenschaft des
 * Verfahrens und macht ihn nicht zu einer Einstellung des Geräts.
 *
 * **Er trägt genau einen Inhalt.** Ein Dialog „Anmeldedaten ändern", der auch das
 * Passwort ändert, ist er nicht: Dafür gibt es keine Gegenstelle. Das Passwort
 * lebt im SilverERP, und die Anmeldeschnittstelle kennt keinen Weg, es zu
 * wechseln.
 *
 * **Zwei Zustände und kein dritter**: eingerichtet, mit dem Weg ihn zu entfernen
 * — oder nicht, mit dem Weg ihn anzulegen. Keine Liste fremder Geräte; was auf
 * diesem Gerät nicht liegt, kann dieser Dialog weder zeigen noch lösen.
 *
 * **Was „entfernen" heißt, steht ausgeschrieben da.** Die Gegenstelle kennt
 * keinen Endpunkt zum Widerrufen eines Zugangsschlüssels; sie hält ihn und ist
 * für ihn die Wahrheit. Entfernt wird deshalb der Zugang auf *diesem* Gerät —
 * der Knopf im Anmeldebild fällt weg. Ein Knopf, der „gelöscht" behauptet und
 * nichts löscht, wäre der schlechtere von beiden.
 *
 * Und deshalb trägt er auch **nicht** die rote Behandlung: Die gilt der einen
 * zerstörenden Handlung, und diese ist keine — was hier weggeht, ist mit einem
 * Knopfdruck wieder da.
 *
 * **Und die Warnung, die kein Produkt von selbst findet:** Ein eingerichteter
 * Zugang hängt an der Adresse, unter der die Anwendung läuft. Ändert sie sich,
 * gelten alle eingerichteten Zugänge schlagartig nicht mehr, ohne dass irgendwo
 * etwas kaputt aussieht — der Knopf steht noch, die Zeremonie scheitert. Dieser
 * Dialog ist der einzige Ort, an dem das jemand liest.
 */
export function CredentialsDialog({
  client,
  onClose,
}: {
  client: PasskeyApi;
  onClose: () => void;
}): ReactElement {
  // Beim Aufbauen gelesen und danach im Zustand geführt: Was der Dialog gerade
  // getan hat, sieht man an ihm selbst und nicht erst nach dem Neuöffnen.
  const [enrolled, setEnrolled] = useState(() => passkeyEnrolled());
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");

  const enrol = (): void => {
    if (busy) {
      return;
    }

    setBusy(true);
    setFailure("");

    setUpPasskey(client).then(
      () => {
        setBusy(false);
        setEnrolled(true);
      },
      (reason: unknown) => {
        setBusy(false);

        // Wer den Gerätedialog wegtippt, hat sich entschieden. Das ist kein
        // Fehlschlag und wird nicht gemeldet.
        if (!(reason instanceof Aborted)) {
          setFailure(describe(reason));
        }
      },
    );
  };

  const remove = (): void => {
    forgetPasskey();
    setEnrolled(false);
    setFailure("");
  };

  return (
    <Modal onClose={onClose}>
      <Dialog
        title="Anmeldedaten ändern"
        description="Die biometrische Anmeldung auf diesem Gerät. Ihr Passwort verwalten Sie im SilverERP."
        width={460}
        onClose={onClose}
        footer={
          <Button variant="outline" size="lg" onClick={onClose}>
            Schließen
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-6)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--sp-5)" }}>
            <span style={{ display: "flex", flex: "none", color: "var(--ico-b)", marginTop: 2 }}>
              <Icon name="fingerprint" size={18} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: "var(--fs-13)",
                  fontWeight: "var(--fw-semibold)",
                  color: "var(--fg-1)",
                }}
              >
                Biometrische Anmeldung
              </div>
              <p
                style={{
                  margin: 0,
                  marginTop: 4,
                  fontSize: "var(--fs-12)",
                  lineHeight: "var(--lh-normal)",
                  color: "var(--fg-7)",
                  textWrap: "pretty",
                }}
              >
                {enrolled
                  ? "Auf diesem Gerät eingerichtet. Im Anmeldebild steht der Knopf „Mit " +
                    "Biometrie anmelden\"; Fingerabdruck oder Gesicht treten an die Stelle des " +
                    "Passworts."
                  : "Auf diesem Gerät nicht eingerichtet. Nach dem Einrichten melden Sie sich hier " +
                    "mit Fingerabdruck oder Gesicht an, statt Ihr Passwort zu tippen."}
              </p>
            </div>
          </div>

          {enrolled ? (
            <p style={hintStyle}>
              Entfernen nimmt den Zugang <strong>auf diesem Gerät</strong> zurück — der Knopf im
              Anmeldebild fällt damit weg. Der Zugangsschlüssel selbst liegt im SilverERP, und die
              Anmeldeschnittstelle kennt keinen Weg, ihn dort zu widerrufen.
            </p>
          ) : null}

          <p style={hintStyle}>
            Ein eingerichteter Zugang gilt nur für die Adresse, unter der diese Anwendung läuft.
            Zieht sie um, gilt er nicht mehr, und Sie richten ihn hier neu ein.
          </p>

          {failure === "" ? null : (
            <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
          )}

          <div>
            {enrolled ? (
              <Button variant="secondary" size="lg" icon="trash-2" onClick={remove}>
                Von diesem Gerät entfernen
              </Button>
            ) : (
              <Button
                variant="primary"
                size="lg"
                icon="fingerprint"
                disabled={busy}
                onClick={enrol}
              >
                {busy ? "Wird eingerichtet ..." : "Auf diesem Gerät einrichten"}
              </Button>
            )}
          </div>
        </div>
      </Dialog>
    </Modal>
  );
}

/** Der erklärende Satz unter dem Zustand — Feinschrift, kein Fehlerband. */
const hintStyle = {
  margin: 0,
  fontSize: "var(--fs-11)",
  lineHeight: "var(--lh-normal)",
  color: "var(--fg-9)",
  textWrap: "pretty",
} as const;
